import { Injectable, isDevMode } from "@angular/core";
import { API, Auth } from "aws-amplify";
import { CognitoUser } from '@aws-amplify/auth';
import { DevAWSSettings, ProdAWSSettings } from "../../awsconfig";
import {
  DashboardTopSendersResponse,
  DashboardOpensAndViewsResponse,
  DashboardTotalSalesMailsResponse,
  TeamResultResponse,
  UserUsageResult,
  RecentsResult,
} from "./dashboard.model";
import { GetClientResponse } from "../shared-models/client.model";
import { GetAppUserResponse } from "../users/users";
import { UsersService } from "../users/users.service";

@Injectable({
  providedIn: "root",
})
export class DashboardService {
  readonly apiName = "SalesMailPortal";
  readonly apiVersion = "2016-04-18";
  readonly awsRegion = "us-east-1";
  public setting = isDevMode() ? new DevAWSSettings() : new ProdAWSSettings();

  constructor(private userService: UsersService) {}

  public async getTopSenders(
    days: number = 90,
    client_id: number
  ): Promise<DashboardTopSendersResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=top-senders-90&i=${days}&client_id=${client_id}`;
    return API.get(this.apiName, path, {});
  }

  public async getOpensAndViews(
    days: number = 90,
    client_id: number
  ): Promise<DashboardOpensAndViewsResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-sent-90&i=${days}&client_id=${client_id}`;
    return API.get(this.apiName, path, {});
  }

  public async getOpensAndViewsStd(
    days: number = 90
  ): Promise<DashboardOpensAndViewsResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-sent-std-90&i=${days}`;
    return API.get(this.apiName, path, {});
  }

  public async getSalesMailsTotals(
    days: number = 90,
    client_id: number
  ): Promise<DashboardTotalSalesMailsResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-total-90&i=${days}&client_id=${client_id}`;
    return API.get(this.apiName, path, {});
  }

  public async getSalesMailsTotalsStd(
    days: number = 90,
    client_id: number
  ): Promise<DashboardTotalSalesMailsResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-total-std-90&i=${days}&client_id=${client_id}`;
    return API.get(this.apiName, path, {});
  }

  public async getTeamResults(
    days: number = 90,
    client_id: number
  ): Promise<TeamResultResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-team-results&i=${days}&client_id=${client_id}`;
    return API.get(this.apiName, path, {});
  }

  public async getStdUserResults(
    days: number = 90,
    client_id: number
  ): Promise<TeamResultResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-user-results&i=${days}&client_id=${client_id}`;
    return API.get(this.apiName, path, {});
  }

  public async getStdReactions(days: number = 90): Promise<TeamResultResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-standard-reactions&i=${days}`;
    return API.get(this.apiName, path, {});
  }

  public async getStdComments(days: number = 90): Promise<TeamResultResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-standard-comments&i=${days}`;
    return API.get(this.apiName, path, {});
  }

  public async getStdCtas(days: number = 90): Promise<TeamResultResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-standard-ctas&i=${days}`;
    return API.get(this.apiName, path, {});
  }

  public async getStdSalesmails(
    days: number = 90
  ): Promise<TeamResultResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-standard-salesmails&i=${days}`;
    return API.get(this.apiName, path, {});
  }

  public async getReactions(
    days: number = 90,
    client_id: number
  ): Promise<TeamResultResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-reactions&i=${days}&client_id=${client_id}`;
    return API.get(this.apiName, path, {});
  }

  public async getComments(
    days: number = 90,
    client_id: number
  ): Promise<TeamResultResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-comments&i=${days}&client_id=${client_id}`;
    return API.get(this.apiName, path, {});
  }

  public async getCtas(
    days: number = 90,
    client_id: number
  ): Promise<TeamResultResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-ctas&i=${days}&client_id=${client_id}`;
    return API.get(this.apiName, path, {});
  }

  public async getSalesmails(
    days: number = 90,
    client_id: number
  ): Promise<TeamResultResponse> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-salesmails&i=${days}&client_id=${client_id}`;
    return API.get(this.apiName, path, {});
  }

  public async getTeamUsage(
    days: number = 90,
    client_id: number
  ): Promise<UserUsageResult> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-all-user-usage&i=${days}&client_id=${client_id}`;
    return API.get(this.apiName, path, {});
  }

  public async getRecentComments<T>(): Promise<RecentsResult<T>> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-recents-comments`;
    return API.get(this.apiName, path, {});
  }

  public async getRecentCTAs<T>(): Promise<RecentsResult<T>> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-recents-ctas`;
    return API.get(this.apiName, path, {});
  }

  public async getRecentReactions<T>(): Promise<RecentsResult<T>> {
    var cognitoUser = await Auth.currentAuthenticatedUser();
    let path = `/dashboard?id=${cognitoUser.getUsername()}&type=sm-recents-reactions`;
    return API.get(this.apiName, path, {});
  }

  public async getRecentCommentsTeam<T>(
    client_id: number
  ): Promise<RecentsResult<T>> {
    let path = `/dashboard?id=${client_id}&type=sm-recents-comments-team`;
    return API.get(this.apiName, path, {});
  }

  public async getRecentCTAsTeam<T>(
    client_id: number
  ): Promise<RecentsResult<T>> {
    let path = `/dashboard?id=${client_id}&type=sm-recents-ctas-team`;
    return API.get(this.apiName, path, {});
  }

  public async getRecentReactionsTeam<T>(
    client_id: number
  ): Promise<RecentsResult<T>> {
    let path = `/dashboard?id=${client_id}&type=sm-recents-reactions-team`;
    return API.get(this.apiName, path, {});
  }

  async getCurrentUsersClientId():Promise<number> {
    let user: CognitoUser = await Auth.currentAuthenticatedUser();    

    const getUserPromise = new Promise<number>((resolve, reject) => {
      if(!user) { reject('No user defined'); }
      user.getUserAttributes((err, result) => {
        if (err) {
            console.error(err);
            reject('No client ID');
        }
        let clientId = result.filter(item => item.getName() == "custom:client_id").map(att => att.getValue())[0].toString();
        resolve(Number(clientId));
      });
    });

    return getUserPromise;    
  }

  /* 
  Checks the current users client and user record. Enable_gamification must be turned 
  for both client and user record in order to be visible - SM-286 
  */
  public async shouldEnableGamification():Promise<boolean> {
    const user: CognitoUser = await Auth.currentAuthenticatedUser();
    const cognito_id = user.getUsername();
    const client_id = await this.getCurrentUsersClientId();
    
    // Get Client
    const apiName = "SalesMailUser";
    const client_path =`/client?client_id=${client_id}`;
    const client_response: GetClientResponse = await API.get(apiName, client_path, {});

    // If not enabled on client exit, no reason to check user
    if (!client_response || client_response.value.length == 0 || !client_response.value[0].enable_gamification) {
      return false;
    }

    // Get user
    const sm_user = await this.userService.getCurrentSalesMailUser();

    if (!sm_user || !sm_user.enable_gamification) {
      return false;
    }

    return true;
  }


}
